import { Button, Tour, TourProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import classes from './HeaderComponent.module.scss';
import Assets from 'assets/icons';
import { setAddDropdownOpen, setIsRSidebarCollapsed, setIsTourOpen, setUploadDropdownOpen } from 'store/slices/windowStateSlice';

type TourElementTypes = {
  isAvailableUploading: boolean;
};

const TourElement = ({isAvailableUploading}: TourElementTypes) => {
  const [currentStep, setCurrentStep] = React.useState(0);

  const { isTourOpen, sideBarWidth, uploadDropdownOpen, addDropdownOpen, isRSidebarCollapsed } = useAppSelector((state) => state.windowState);
  const { userPermissions, available_rooms } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);  
  const { userData } = useAppSelector((store) => store.userData);  

  const isConfidentRoom = dataRoom?.agreement_file && dataRoom?.user_status === 2;
  const isExperimentalAvailable = userData?.is_experimental_available;
  const isAvailableInviteUsers = userPermissions?.can_invite_users;
  const isAvailableSettings = userPermissions?.can_edit_room_settings;
  const isAvailableToEditRoles = userPermissions?.can_edit_roles || userPermissions?.can_create_group_all_roles;
  const isAvailablePermissionsSettings = userPermissions?.can_edit_folder_permissions;
  const isAvailableViewLogs = userPermissions?.can_view_logs;
  const isOwnerOrFullAdmin = available_rooms.some(room => room.id === dataRoom?.id && room.is_admin);

  const getRightSteps = React.useCallback(() => {
    const stepsWithOpenUploadMenu = [];
    const stepsWithOpenAddMenu = [];
    const stepsRightSidebar = [];

    if (isAvailableUploading) {
      stepsWithOpenUploadMenu.push(4);
      stepsWithOpenUploadMenu.push(5);
      if (isConfidentRoom) {
        stepsWithOpenUploadMenu.push(6);
        isExperimentalAvailable && stepsWithOpenUploadMenu.push(7);
      } else {
        isExperimentalAvailable && stepsWithOpenUploadMenu.push(6);
      }
    };

    const lastStepInUploadMenu = stepsWithOpenUploadMenu[stepsWithOpenUploadMenu.length - 1];    
    if (!lastStepInUploadMenu) {
      stepsWithOpenAddMenu.push(5);
    } else if (userPermissions.can_invite_users) {      
      stepsWithOpenAddMenu.push(lastStepInUploadMenu+2, lastStepInUploadMenu+3)
    } else {
      stepsWithOpenAddMenu.push(lastStepInUploadMenu+2)
    };

    let lastStepInAddMenu = stepsWithOpenAddMenu[stepsWithOpenAddMenu.length - 1];
    let stepCounter = lastStepInAddMenu + 1;
    
    stepsRightSidebar.push(++stepCounter);
    stepsRightSidebar.push(++stepCounter);
    isAvailableSettings && stepsRightSidebar.push(++stepCounter);
    isAvailableToEditRoles && stepsRightSidebar.push(++stepCounter);
    isAvailablePermissionsSettings && stepsRightSidebar.push(++stepCounter);
    isAvailableViewLogs && stepsRightSidebar.push(++stepCounter);
    isOwnerOrFullAdmin && stepsRightSidebar.push(++stepCounter);
    isOwnerOrFullAdmin && stepsRightSidebar.push(++stepCounter);

    return {stepsWithOpenUploadMenu, stepsWithOpenAddMenu, stepsRightSidebar}
  },[isAvailableUploading, isExperimentalAvailable, isConfidentRoom, userPermissions, available_rooms]);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const filesBtn = <Button
    size='middle'
    shape='default'
    style={{cursor: 'default', pointerEvents: 'none', margin: '0 5px'}}
    type='primary'
    color='primary'
  >
    {t('Documents.management.addFiles')}
  </Button>;

  const plusBtn = <Button
    className={classes.plusButton}
    icon={<Assets.PlusIcon/>}
    style={{cursor: 'default', pointerEvents: 'none', margin: '0 5px'}}
    shape='circle'
    size='small'
    type='primary'
  />

  const settingBtn = <Button
    className={classes.settingButton}
    style={{cursor: 'default', pointerEvents: 'none', margin: '0 2px'}}
    icon={<Assets.SettingsIcon/>}
    type='text'
    size='small'
  />

  const getSteps = React.useCallback(() => {
    const steps: TourProps['steps'] = [
      {
        title: t('Settings.tips.roomTitle'),
        description: (<div className={classes.descRow}>{t('Settings.tips.roomsDescription')}</div>),
        target: document.getElementById('sidebar-rooms-tab'),
        placement: 'rightTop',
        arrow: {pointAtCenter: false},
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
      },
      {
        title: t('Settings.tips.filtersTitle'),
        description: (<div className={classes.descRow}>{t('Settings.tips.filtersDescription')}</div>),
        target: document.getElementById('rooms-filter-btn'),
        placement: 'rightTop',
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
      },
      {
        title: t('Settings.tips.chatsTitle'),
        description: (<div className={classes.descRow}>{t('Settings.tips.chatsDescription')}</div>),
        target: document.getElementById('all-chats-btn'),
        placement: 'rightTop',
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
      }
    ];
  
    if (isAvailableUploading) {
      steps.push({
        title: t('Settings.tips.addFilesTitle'),
        description: <div className={classes.descRow}>
          <span>{t('Settings.tips.pressBtn')}</span>{filesBtn}<span>{t(`Settings.tips.${isConfidentRoom ? 'addFilesChoose' : 'addFilesUpload'}`)}</span>
        </div>,
        target: document.getElementById('room-toolbar-add-files'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
      });
      steps.push({
        title: t('Settings.tips.addFilesTitle'),
        description: <div className={classes.descRow}>{t('Settings.tips.addFilesChooseSimple')}</div>,
        target: document.getElementById('upload-popup-container-1'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
        placement: 'leftTop',
        arrow: {pointAtCenter: false},
      });
      isConfidentRoom && steps.push({
        title: t('Settings.tips.addFilesTitle'),
        description: <div className={classes.descRow}>{t('Settings.tips.addFilesChooseConfident')}</div>,
        target: document.getElementById('upload-popup-container-2'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
        placement: 'leftTop',
        arrow: {pointAtCenter: false},
      })
      steps.push({
        title: t('Settings.tips.createFileTitle'),
        description: <div className={classes.descRow}>{t('Settings.tips.createFileDescription')}</div>,
        target: document.getElementById('upload-popup-container-3'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
        placement: 'leftTop',
        arrow: {pointAtCenter: false},
      });
      isExperimentalAvailable && steps.push({
        title: t('Settings.tips.generateFileTitle'),
        description: <div className={classes.descRow}>{t('Settings.tips.generateFileDescription')}</div>,
        target: document.getElementById('upload-popup-container-4'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
        placement: 'leftTop',
        arrow: {pointAtCenter: false},
      });
    } else {
      steps.push({
        title: t('Settings.tips.addFilesTitle'),
        description: <div className={classes.descRow}>{t('Settings.tips.cantAddFiles')}</div>,
        target: document.getElementById('room-toolbar-add-files'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
      })
    };
  
    steps.push({
      title: t(`Settings.tips.${isAvailableInviteUsers ? 'addUsersFoldersTitle' : 'addFoldersTitle' }`),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.pressBtn')}</span>{plusBtn}
      </div>,
      target: document.getElementById('room-toolbar-add-menu'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    })
    
    steps.push({
      title: t('Settings.tips.newFoldersTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.newFolderDescription')}</span>
      </div>,
      target: document.getElementById('add-popup-container-1'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
      placement: 'leftTop',
      arrow: {pointAtCenter: false},
    })

    if (isAvailableInviteUsers) {
      steps.push({
        title: t('Settings.tips.addUserTitle'),
        description: <div className={classes.descRow}>
          <span>{t('Settings.tips.addUsersChoose')}</span>
        </div>,
        target: document.getElementById('add-popup-container-2'),
        nextButtonProps: {children: t('Settings.tips.next')},
        prevButtonProps:  {children: t('Settings.tips.prev')},
        placement: 'leftTop',
        arrow: {pointAtCenter: false},
      })
    };
  
    steps.push({
      title: t('Settings.tips.roomSettingsTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.pressBtn')}</span>{settingBtn}<span>{t('Settings.tips.openSettings')}</span>
      </div>,
      target: document.getElementById('room-toolbar-settings'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    steps.push({
      title: t('Settings.tips.ndaTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.ndaDescription')}</span>
      </div>,
      target: document.getElementById('rsb-nda-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isAvailableSettings && steps.push({
      title: t('Settings.tips.watermarkTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.watermarkDescription')}</span>
      </div>,
      target: document.getElementById('rsb-watermark-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isAvailableToEditRoles && steps.push({
      title: t('Settings.tips.editRolesTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.editRolesDescription')}</span>
      </div>,
      target: document.getElementById('rsb-roles-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isAvailablePermissionsSettings && steps.push({
      title: t('Settings.tips.permissionsTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.permissionsDescription')}</span>
      </div>,
      target: document.getElementById('rsb-permissions-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isAvailableViewLogs && steps.push({
      title: t('Settings.tips.viewLogsTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.viewLogsDescription')}</span>
      </div>,
      target: document.getElementById('rsb-logs-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isOwnerOrFullAdmin && steps.push({
      title: t('Settings.tips.recycleBinTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.recycleBinDescription')}</span>
      </div>,
      target: document.getElementById('rsb-recycle-bin-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    isOwnerOrFullAdmin && steps.push({
      title: t('Settings.tips.messangerTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.messangerDescription')}</span>
      </div>,
      target: document.getElementById('rsb-messanger-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    steps.push({
      title: t('Settings.tips.notificationTitle'),
      description: <div className={classes.descRow}>
        <span>{t('Settings.tips.notificationDescription')}</span>
      </div>,
      target: document.getElementById('rsb-notification-section'),
      nextButtonProps: {children: t('Settings.tips.next')},
      prevButtonProps:  {children: t('Settings.tips.prev')},
    });

    if (steps[steps.length - 1]?.nextButtonProps?.children) {    
      steps[steps.length - 1].nextButtonProps!.children = t('Settings.tips.final');
    };
    return steps
  }, [userPermissions, isAvailableUploading, sideBarWidth, isExperimentalAvailable, uploadDropdownOpen, addDropdownOpen, isRSidebarCollapsed, available_rooms]);

  const onChangeStep = (current: number) => {    
    const {stepsWithOpenUploadMenu, stepsWithOpenAddMenu, stepsRightSidebar} = getRightSteps();

    if (isAvailableUploading && stepsWithOpenUploadMenu.includes(current)) {
      dispatch(setUploadDropdownOpen(true));
      dispatch(setAddDropdownOpen(false));
      dispatch(setIsRSidebarCollapsed({ collapsed: true, entity: '', width: 0 }));
      setTimeout(() => {
        setCurrentStep(current);
      }, 200);
    } else if (stepsWithOpenAddMenu.includes(current)) {
      dispatch(setAddDropdownOpen(true));
      dispatch(setUploadDropdownOpen(false));
      dispatch(setIsRSidebarCollapsed({ collapsed: true, entity: '', width: 0 }));
      setTimeout(() => {        
        setCurrentStep(current);
      }, 200);
    } else if (stepsRightSidebar.includes(current)) {
      dispatch(setAddDropdownOpen(false));
      dispatch(setUploadDropdownOpen(false));
      dispatch(setIsRSidebarCollapsed({ collapsed: false, entity: 'room_setting', width: 300 }));
      setTimeout(() => {
        setCurrentStep(current);
      }, 300);
    } else {
      dispatch(setUploadDropdownOpen(false));
      dispatch(setAddDropdownOpen(false));
      dispatch(setIsRSidebarCollapsed({ collapsed: true, entity: '', width: 0 }));
      const currentTimeout = currentStep > current && stepsRightSidebar.includes(currentStep) ? 300 : 10
      setTimeout(() => {
        setCurrentStep(current);
      }, currentTimeout);
    };
  };

  const closeTips = () => {
    setCurrentStep(0);
    dispatch(setIsTourOpen(false));
    dispatch(setUploadDropdownOpen(false));
    dispatch(setAddDropdownOpen(false));
    dispatch(setIsRSidebarCollapsed({ collapsed: true, entity: '', width: 0 }));
  };  

  return (
    <Tour
      open={isTourOpen}
      onClose={closeTips}
      steps={getSteps()}
      scrollIntoViewOptions={{ behavior: 'smooth', block: 'nearest' }}
      placement={'bottomRight'}
      arrow={{pointAtCenter: true}}
      onChange={onChangeStep}
      current={currentStep}
      disabledInteraction={true}
      zIndex={2000}
      keyboard={true}
    />
  );
}

export default TourElement;
